import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import useAuthApi, { AUTH_QUERIES } from "../../apiHooks/useAuthApi";
import useUserPermissionsApi from "../../apiHooks/useUserPermissionsApi";
import useApi from "../../common/hooks/useApi";
import Loader from "../../common/loader";
import { MODULES } from "../../permissions/types/permission-types";
import { ROUTES } from "../navigation";
import { KEY_TOKEN, KEY_USER_TYPE } from "../variables";

const AuthContext = React.createContext({
  allPermissions: [],
  isAuthorizedToAccess: false,
  fetchAuthorityForModule: {},
  isPermissionLoading: true,
  isAuthenticated: false,
  login: undefined,
  logout: undefined,
  getToken: undefined,
  isLoading: false,
  getUserType: undefined,
  loggedUser: undefined,
  isCustomer: false,
  userType: null,
  initPermissionsForUser: undefined,
});

export const useAuthContext = () => useContext(AuthContext);

export const AuthContextProvider = ({ children }) => {
  // const ALL_PERMISSIONS = Object.keys(PERMISSION_FEATURES).map(
  //   (key) => PERMISSION_FEATURES[key]
  // );

  const [allPermissions, setAllPermissions] = useState({
    [MODULES.EDO]: [],
    [MODULES.MDO]: [],
    [MODULES.ACCESS_MANAGEMENT]: [],
    [MODULES.USER_MANAGEMENT]: [],
    // edo: ALL_PERMISSIONS,
    // dashboard: ALL_PERMISSIONS,
    // settings: ALL_PERMISSIONS,
    // userManagement: ALL_PERMISSIONS,
    // mdo: ALL_PERMISSIONS,
    // accessManagement: ALL_PERMISSIONS,
  });

  const [token, setToken] = useState(null);

  const {
    userLoginApi: { execute: userLogin, isLoading: isUserLoginLoading },
    customerLoginApi: {
      execute: customerLogin,
      isLoading: isCustomerLoginLoading,
    },
    loggedUserApi: {
      execute: getLoggedUser,
      isLoading: isLoggedUserLoading,
      data: loggedUser,
    },
  } = useAuthApi([AUTH_QUERIES.getLoggedUser]);

  const {
    getUserPermissionApi: { execute: getUserPermission },
  } = useUserPermissionsApi();

  const {
    trigger: getUserPermissionApiTrigger,
    isLoading: isFetchingUserPermissions,
  } = useApi(getUserPermission);

  const formatPermissions = useCallback((data) => {
    if (!Array.isArray(data) || data.length === 0) return;

    const groupedPermissions = data.reduce(
      (acc, { moduleName, permissionName, isGranted }) => {
        if (!isGranted) return acc;
        const module = moduleName.replace(/\s+/g, "");
        (acc[module] ||= []).push(permissionName);
        return acc;
      },
      {}
    );

    setAllPermissions(groupedPermissions);
  }, []);

  const navigate = useNavigate();

  const initPermissionsForUser = useCallback(
    async (userId) => {
      const permissions = await getUserPermissionApiTrigger(
        userId ?? loggedUser?.userID
      );
      formatPermissions(permissions);
    },
    [formatPermissions, getUserPermissionApiTrigger, loggedUser?.userID]
  );

  const initLoggedUser = useCallback(async () => {
    const res = await getLoggedUser();

    if (res?.data?.userID) {
      initPermissionsForUser(res?.data?.userID);
    }
  }, [getLoggedUser, initPermissionsForUser]);

  const isAuthorizedToAccess = useCallback(
    (module, permission) => {
      const moduleArr = allPermissions[module];
      if (!moduleArr) return false;

      const moduleSet = new Set(moduleArr);
      return permission ? moduleSet.has(permission) : moduleArr.length > 0;
    },
    [allPermissions]
  );

  const fetchAuthorityForModule = useCallback(
    (module) => {
      const moduleArr = allPermissions[module];
      if (!moduleArr) return {};

      return Object.fromEntries(moduleArr.map((item) => [item, true]));
    },
    [allPermissions]
  );

  const setUserType = useCallback(
    (type) => localStorage.setItem(KEY_USER_TYPE, type),
    []
  );

  const getUserType = useCallback(
    () => localStorage.getItem(KEY_USER_TYPE),
    []
  );

  const userType = useMemo(
    () => (loggedUser?.customerID ? "customer" : "user"),
    [loggedUser]
  );

  //type = user | customer
  const login = useCallback(
    async (data, type) => {
      const res = await (type === "seaCare"
        ? userLogin(data)
        : customerLogin(data));
      if (res) {
        setToken(res.token);
        setUserType(type);
        localStorage.setItem(KEY_TOKEN, res.token);
        localStorage.setItem(KEY_USER_TYPE, type);

        await initLoggedUser();

        navigate(ROUTES.dashboard);
      }
    },
    [userLogin, customerLogin, setUserType, initLoggedUser, navigate]
  );

  const logout = useCallback(() => {
    const isStaff = !loggedUser?.customerID;
    setToken(null);

    localStorage.removeItem(KEY_TOKEN);
    localStorage.removeItem(KEY_USER_TYPE);

    navigate(isStaff ? ROUTES.staffLogin : ROUTES.customerLogin);
  }, [navigate, loggedUser?.customerID]);

  const getToken = useCallback(() => token, [token]);

  const isCustomer = useMemo(
    () => !!loggedUser?.customerID,
    [loggedUser?.customerID]
  );

  useEffect(() => {
    const storedToken = localStorage.getItem(KEY_TOKEN);
    const isLogin = [
      ROUTES.userLogin,
      ROUTES.customerLogin,
      ROUTES.staffLogin,
      ROUTES.changePassword,
      ROUTES.forgotPassword,
    ].includes(window.location.pathname);

    if (!storedToken) {
      if (!isLogin) logout();
      return;
    }

    setToken(storedToken);
    initLoggedUser();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (token) {
      localStorage.setItem(KEY_TOKEN, token);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <AuthContext.Provider
      value={{
        allPermissions,
        isAuthorizedToAccess,
        isPermissionLoading: false,
        token,
        login,
        isAuthenticated: !!token,
        logout,
        getToken,
        isLoading:
          isUserLoginLoading ||
          isCustomerLoginLoading ||
          isFetchingUserPermissions,
        getUserType,
        loggedUser,
        isCustomer,
        userType,
        fetchAuthorityForModule,
        initPermissionsForUser,
      }}
    >
      {isLoggedUserLoading ? <Loader /> : children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthContextProvider");
  }
  return context;
};
